import { useAuthStore } from '@/store/authStore';
import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect, useRef } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { ROUTES } from '@/config/routes';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  /**
   * Permission required to view this route. Omit for routes that only need a
   * logged-in user. Purely so the UI does not offer a page the API will
   * refuse — the API is what actually protects the data.
   */
  requiredPermission?: string | string[];
  match?: 'all' | 'any';
}

export function ProtectedRoute({
  children,
  requiredPermission,
  match = 'all',
}: ProtectedRouteProps) {
  const { keycloak, initialized } = useKeycloak();
  const { isInitialized } = useAuthStore();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const redirectingRef = useRef(false);

  useEffect(() => {
    if (!initialized || !isInitialized) return;
    if (keycloak.authenticated) return;
    if (redirectingRef.current) return;

    // No session locally. Trigger Keycloak's standard auth flow:
    // - If a Keycloak SSO session exists (set by another app like cert-web),
    //   the browser silently redirects back with a code, no password prompt.
    // - If not, the user gets Keycloak's login form.
    // Either way, the user lands back on the page they originally requested.
    redirectingRef.current = true;
    void keycloak.login({ redirectUri: window.location.href });
  }, [initialized, isInitialized, keycloak]);

  if (!initialized || !isInitialized || !keycloak.authenticated) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (requiredPermission) {
    const required = Array.isArray(requiredPermission)
      ? requiredPermission
      : [requiredPermission];
    const allowed =
      match === 'any' ? required.some(hasPermission) : required.every(hasPermission);
    if (!allowed) return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
